import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Text, Link } from '../../components/index.js'
import { HeaderNavigationMenu } from './types.js'
import { useNavContext } from './NavProvider.js'
import { MenuItem, SubSubCategoryItemComp, SubSubCategoryContentComp } from './MobileMenuComponents.js'
import { useAiuiContext } from '../AiuiProvider/index.js'
import { getLocalizedPath } from '../../helpers/utils.js'

/**
 * 移动端侧边栏下拉
 * @param sidebarCategoriesMetadata 侧边栏分类元数据
 * @param seriesMetadata 侧边栏系列元数据
 */
export const MobileSidebarMenu = ({
  sidebarCategoriesMetadata,
  seriesMetadata,
}: {
  sidebarCategoriesMetadata: any
  seriesMetadata: any
}) => {
  const {
    currentMenu,
    setCurrentMenu,
    subSubCategory,
    setSubSubCategory,
    setCurrentSeriesMetadata,
    currentSeriesMetadata,
    buildProps,
    onSidebarNavClick,
  } = useNavContext()
  const { locale = 'us' } = useAiuiContext()
  const [expandedSubcategory, setExpandedSubcategory] = useState<{ index: number; open: boolean }[]>([])
  const [expandedSeriesCategories, setExpandedSeriesCategories] = useState<{ index: number; open: boolean }[]>([])

  useEffect(() => {
    if (sidebarCategoriesMetadata?.subcategories?.length) {
      setExpandedSubcategory(
        sidebarCategoriesMetadata?.subcategories?.map((_: any, index: number) => ({
          index,
          open: false,
        }))
      )
    }
  }, [sidebarCategoriesMetadata])

  useEffect(() => {
    if (subSubCategory?.subSubCategories?.length) {
      setExpandedSeriesCategories(
        subSubCategory?.subSubCategories?.map((_: any, index: number) => ({
          index,
          open: index === 0,
        }))
      )
    }
  }, [subSubCategory])

  const handleSubSubCategoryClick = useCallback(
    (item: any, subIndex?: number) => {
      setCurrentMenu && setCurrentMenu(HeaderNavigationMenu.Third)
      setSubSubCategory?.(item)
      const curSeriesMetadata =
        seriesMetadata?.find((seriesItem: any) =>
          subIndex !== undefined
            ? item?.subSubCategories?.[subIndex]?.label?.toLowerCase() === seriesItem.label?.toLowerCase()
            : item?.label?.toLowerCase() === seriesItem.label?.toLowerCase()
        ) || {}
      // 通过categoriesMetadata的subSubCategories的label与series的label匹配
      if (item?.collections) {
        const category = buildProps?.categories?.[item?.collections] || {}
        setCurrentSeriesMetadata?.({
          label: curSeriesMetadata?.label,
          isCollection: true,
          banner: curSeriesMetadata?.banner,
          primary: curSeriesMetadata?.primary,
          series: [
            {
              products: category?.products,
            },
          ],
        })
      } else {
        setCurrentSeriesMetadata?.(curSeriesMetadata)
      }
    },
    [seriesMetadata, setCurrentMenu, setSubSubCategory, setCurrentSeriesMetadata, buildProps?.categories]
  )

  const MobileSidebarMenuComp = useMemo(() => {
    switch (currentMenu) {
      case HeaderNavigationMenu.Secondary:
        return (
          // 二级菜单
          <div className="tablet:px-8 laptop:px-16 flex h-full flex-col gap-8 p-4">
            <div>
              {sidebarCategoriesMetadata?.subcategories?.map((item: any, index: number) => {
                return (
                  <div key={`${item.label}-${index}`}>
                    <MenuItem
                      label={item?.label}
                      active={expandedSubcategory.find(item => item.index === index)?.open}
                      onClick={() => {
                        handleSubSubCategoryClick(item)
                        onSidebarNavClick?.(item, index)
                      }}
                    />
                  </div>
                )
              })}
              {sidebarCategoriesMetadata?.guide?.label && (
                <Link href={getLocalizedPath(sidebarCategoriesMetadata?.guide?.url, locale)}>
                  <div className="mt-4">
                    <Text
                      html={sidebarCategoriesMetadata?.guide?.label}
                      className="text-sm font-bold leading-[1.2] text-[#6D6D6F]"
                    />
                  </div>
                </Link>
              )}
            </div>
            <div className="tablet:items-start flex flex-col items-center gap-4">
              {sidebarCategoriesMetadata?.primary && (
                <Button
                  as="a"
                  href={`${getLocalizedPath(sidebarCategoriesMetadata?.primary?.url, locale)}?ref=${sidebarCategoriesMetadata?.primary?.label}_shopall`}
                  variant="primary"
                  size="lg"
                  className="tablet:w-auto w-full text-base"
                >
                  {sidebarCategoriesMetadata?.primary?.label}
                </Button>
              )}
              {sidebarCategoriesMetadata?.secondary && (
                <Button
                  as="a"
                  href={getLocalizedPath(sidebarCategoriesMetadata?.secondary?.url, locale)}
                  variant="link"
                  size="lg"
                  className="tablet:w-auto tablet:px-0 w-full py-0 text-base"
                >
                  {sidebarCategoriesMetadata?.secondary?.label}
                </Button>
              )}
            </div>
          </div>
        )
      case HeaderNavigationMenu.Third:
        return (
          // 三级菜单
          <div className="tablet:px-8 laptop:px-16 p-4">
            {subSubCategory?.subSubCategories ? (
              subSubCategory?.subSubCategories?.map((subSubCategoryItem: any, index: number) => {
                const curSeriesMetadata =
                  seriesMetadata?.find(
                    (item: any) => item.label.toLowerCase() === subSubCategoryItem?.label?.toLowerCase()
                  ) || {}
                let matchSeriesMetadata = {} as any
                if (subSubCategoryItem?.collections) {
                  const category = buildProps?.categories?.[subSubCategoryItem?.collections] || {}
                  matchSeriesMetadata = {
                    label: curSeriesMetadata?.label,
                    isCollection: true,
                    banner: curSeriesMetadata?.banner,
                    primary: curSeriesMetadata?.primary,
                    series: [
                      {
                        products: category?.products,
                      },
                    ],
                  }
                } else {
                  matchSeriesMetadata = curSeriesMetadata
                }
                return (
                  <div key={`${subSubCategoryItem.label}-${index}`}>
                    {Reflect.ownKeys(subSubCategoryItem).length > 0 && (
                      <SubSubCategoryItemComp
                        matchSeriesMetadata={matchSeriesMetadata}
                        onSubSubCategoryItemClick={() => {
                          onSidebarNavClick?.(subSubCategoryItem, index)
                          setExpandedSeriesCategories(prev =>
                            prev.map((item, i) => ({ ...item, open: i === index ? !item.open : item.open }))
                          )
                        }}
                        expanded={!!expandedSeriesCategories?.find(item => item.index === index)?.open}
                      />
                    )}
                    {expandedSeriesCategories?.find(item => item.index === index)?.open && (
                      <>
                        <SubSubCategoryContentComp matchSeriesMetadata={matchSeriesMetadata} />
                        {matchSeriesMetadata?.primary && (
                          <div className="my-4 text-center">
                            <Button
                              as="a"
                              href={getLocalizedPath(matchSeriesMetadata?.primary?.url, locale)}
                              className="text-base leading-[1.2] no-underline"
                              variant="secondary"
                              size="base"
                            >
                              {matchSeriesMetadata?.primary?.label}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )
              })
            ) : (
              <>
                {Reflect.ownKeys(currentSeriesMetadata).length > 0 && (
                  <SubSubCategoryItemComp matchSeriesMetadata={currentSeriesMetadata} />
                )}
                <SubSubCategoryContentComp matchSeriesMetadata={currentSeriesMetadata} />
                {currentSeriesMetadata?.primary && (
                  <div className="my-4 text-center ">
                    <Button
                      as="a"
                      href={`${getLocalizedPath(currentSeriesMetadata?.primary?.url, locale)}?ref=${currentSeriesMetadata?.label}_viewmore`}
                      variant="secondary"
                      size="base"
                      className="text-base leading-[1.2] no-underline"
                    >
                      {currentSeriesMetadata?.primary?.label}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )
      default:
        return null
    }
  }, [
    currentMenu,
    sidebarCategoriesMetadata,
    seriesMetadata,
    expandedSubcategory,
    expandedSeriesCategories,
    subSubCategory,
    currentSeriesMetadata,
    buildProps?.categories,
    handleSubSubCategoryClick,
    onSidebarNavClick,
    locale,
  ])

  return MobileSidebarMenuComp
}
